import React, { useState, useEffect } from "react";
import { Download, FileText, Filter, X } from "lucide-react";
import "../styles/EmpReports.css";
import apiClient from "../api/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import axiosInstance from "../api/api";
import { DateTime } from "luxon";

const Modal = ({ isOpen, onClose, title, message, type = "info" }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className={`modal-header ${type}`}>
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="modal-btn" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const londonDate = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

// const londonTime = new Intl.DateTimeFormat("en-GB", {
//   hour: "2-digit",
//   minute: "2-digit",
//   hour12: false,
// });

const formatDate = (dateTime) => {
  if (!dateTime) return "--:--";

  try {
    const londonTime = DateTime.fromISO(dateTime, {
      zone: "Europe/London",
    });

    return londonTime.toFormat("HH:mm");
  } catch (err) {
    console.error("Time format error:", err);
    return "--:--";
  }
};

const formatHours = (input) => {
  if (input === null || input === undefined) return "--";

  // If input looks like milliseconds (big number), convert to decimal hours
  // Here I assume input > 24 (hours) means milliseconds, adjust as needed
  const decimalHours = input > 24 ? input / 3600000 : input;

  // Prevent negative values - return "--" instead of negative working hours
  if (decimalHours < 0) return "--";

  const totalMinutes = Math.floor(decimalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
};

const timeToHours = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours + minutes / 60;
};

const EmpReports = () => {
  const [reportType, setReportType] = useState("attendance");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [employeeId, setEmployeeId] = useState("all");
  const [attendanceType, setAttendanceType] = useState("employee"); // State for attendance type filter
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allOutEmployees, setAllOutEmployees] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [adminSettings, setAdminSettings] = useState(null);
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  const showModal = (title, message, type = "info") => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  useEffect(() => {
    const fetchOutEmployees = async () => {
      try {
        const response = await axiosInstance.get("/employees/admin");
        setAllOutEmployees(response.data);
      } catch (error) {
        console.error("Failed to fetch Outsource employees:", error);
      }
    };

    fetchOutEmployees();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axiosInstance.get("/employees");
        setAllEmployees(response.data);
      } catch (error) {
        console.error("Failed to fetch employees:", error);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        const res = await apiClient.get("/leave/approved");
        setLeaveRequests(res.data || []);
      } catch (err) {
        console.error("Error fetching leave requests:", err);
      }
    };
    fetchLeaveRequests();
  }, []);

  const getLeaveTypeForDate = (empId, date) => {
    const target = date.toISOString().split("T")[0]; // YYYY-MM-DD
    const leave = leaveRequests.find(lr => {
      const from = lr.start_date.split("T")[0];
      const to = lr.end_date.split("T")[0];
      return (
        lr.user_id === empId &&
        target >= from &&
        target <= to
      );
    });
    return leave ? leave.type : null; // Adjust property names based on your API
  };

  const closeModal = () => {
    setModal({
      isOpen: false,
      title: "",
      message: "",
      type: "info",
    });
  };

  // Filter states
  const [filters, setFilters] = useState({
    status: "all",
    dayOfWeek: "all",
    lateArrivals: false,
    earlyDepartures: false,
    overtime: false,
    perfectAttendance: false,
    workingDaysOnly: false,
    nonWorkingDaysOnly: false,
    searchName: "",
    sortBy: "date",
    sortOrder: "asc",
  });

  // Fetch holidays on component mount
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await apiClient.get("/holidays");
        setHolidays(res.data || []);
      } catch (err) {
        console.error("Error fetching holidays:", err);
        setError("Failed to fetch holidays data");
      }
    };
    fetchHolidays();
  }, []);

  // Fetch admin settings on component mount
  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const res = await apiClient.get("/admin-settings");
        setAdminSettings(res.data);
      } catch (err) {
        console.error("Error fetching admin settings:", err);
      }
    };
    fetchAdminSettings();
  }, []);

  // Apply filters whenever reportData or filters change
  useEffect(() => {
    applyFilters();
  }, [reportData, filters]);

  // Check if a date is a holiday (including weekends)
  const isHoliday = (date) => {
    const dayOfWeek = date.getDay();

    // Check if it's weekend (Saturday = 6, Sunday = 0)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return true;
    }

    // Check if it's a declared holiday
    const dateISOString = date.toISOString().split("T")[0];
    return holidays.some((holiday) => holiday.date === dateISOString);
  };

  // Check if a date is a working day
  const isWorkingDay = (dateString) => {
    const date = new Date(dateString.split("/").reverse().join("-"));
    return !isHoliday(date);
  };

  const applyFilters = () => {
    let filtered = [...reportData];

    // Status filter
    if (filters.status !== "all") {
      filtered = filtered.filter((row) => row.status === filters.status);
    }

    // Day of week filter
    if (filters.dayOfWeek !== "all") {
      filtered = filtered.filter((row) => {
        const date = new Date(row.date.split("/").reverse().join("-"));
        const dayOfWeek = date.getDay();
        return dayOfWeek.toString() === filters.dayOfWeek;
      });
    }

    // Working/Non-working days filters
    if (filters.workingDaysOnly) {
      filtered = filtered.filter((row) => isWorkingDay(row.date));
    }
    if (filters.nonWorkingDaysOnly) {
      filtered = filtered.filter((row) => !isWorkingDay(row.date));
    }

    // Special condition filters
    if (filters.lateArrivals) {
      filtered = filtered.filter((row) => {
        if (row.clock_in === "-") return false;
        const clockIn = DateTime.fromFormat(row.clock_in, "HH:mm");
        return clockIn.isValid && clockIn > DateTime.fromFormat("09:05", "HH:mm");
      });
    }

    if (filters.earlyDepartures) {
      filtered = filtered.filter((row) => {
        if (row.clock_out === "-") return false;
        const clockOut = DateTime.fromFormat(row.clock_out, "HH:mm");
        return clockOut.isValid && clockOut < DateTime.fromFormat("17:00", "HH:mm");
      });
    }


    if (filters.overtime) {
      filtered = filtered.filter((row) => row.status === "Overtime");
    }


    if (filters.perfectAttendance) {
      filtered = filtered.filter((row) => row.status === "Present");
    }

    // Name search filter
    if (filters.searchName !== "") {
      filtered = filtered.filter((row) =>
        row.name.toLowerCase().includes(filters.searchName.toLowerCase())
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (filters.sortBy) {
        case "name":
          aValue = a.name || "";
          bValue = b.name || "";
          break;
        case "date":
          aValue = new Date(a.date.split("/").reverse().join("-"));
          bValue = new Date(b.date.split("/").reverse().join("-"));
          break;
        case "working_hours":
          aValue = parseFloat(a.working_hours) || 0;
          bValue = parseFloat(b.working_hours) || 0;
          break;
        case "status":
          aValue = a.status || "";
          bValue = b.status || "";
          break;
        case "clock_in":
          aValue = a.clock_in || "";
          bValue = b.clock_in || "";
          break;
        default:
          aValue = a.id || 0;
          bValue = b.id || 0;
      }

      if (filters.sortOrder === "desc") {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      } else {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
    });

    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      dayOfWeek: "all",
      lateArrivals: false,
      earlyDepartures: false,
      overtime: false,
      perfectAttendance: false,
      workingDaysOnly: false,
      nonWorkingDaysOnly: false,
      searchName: "",
      sortBy: "date",
      sortOrder: "asc",
    });
    setCurrentPage(1);
  };

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredData.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getFilteredEmployees = () => {
    if (attendanceType === "employee") {
      return allEmployees.filter((emp) => emp?.user?.role === "employee");
    } else if (attendanceType === "outsource") {
      return allOutEmployees.filter((emp) => emp?.user?.role === "outsource");
    }
    return [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromDate || !toDate) {
      setError("Please select both from and to dates");
      return;
    }

    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (from > to) {
      setError("From date cannot be after To date");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Choose endpoint based on attendance type
      const endpoint =
        attendanceType === "outsource"
          ? `/attendance/outsummary`
          : `/attendance/summary`;

      // Send startDate and endDate in query params
      const res = await apiClient.get(endpoint);

      const all = res.data || [];

      // Filter by selected employee
      const filteredByEmp = all.filter(
        (emp) => employeeId === "all" || emp.id === parseInt(employeeId)
      );

      const formatted = [];

      filteredByEmp.forEach((emp) => {
        // Generate all dates in the range - but not future dates
        const dateRange = [];
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of today

        for (
          let d = new Date(from);
          d <= to && d <= today;
          d.setDate(d.getDate() + 1)
        ) {
          dateRange.push(new Date(d));
        }

        dateRange.forEach((date) => {
          const isHolidayOrWeekend = isHoliday(date);

          // For outsource users, get ALL attendance entries for this date
          // For employees, get only the first (they should have only one per day)
          const dateEntries = emp.attendance?.filter((att) => {
            if (!att.clock_in) return false; // skip null entries

            // Use att.date field directly (database stored date)
            const recordDate = new Date(att.clock_in);
            const match = recordDate.toDateString() === date.toDateString();

            return match;
          }) || [];


          // For employees, use only the first entry (should be only one)
          // For outsource, use all entries
          const entriesToProcess = attendanceType === "outsource" ? dateEntries : dateEntries.slice(0, 1);

          if (entriesToProcess.length === 0) {
            // No attendance entry for this date
            let statusIfAbsent = "Absent";
            if (!isHolidayOrWeekend) {
              const leaveType = getLeaveTypeForDate(emp.id, date);
              if (leaveType) {
                statusIfAbsent = leaveType;
              }
            }
            const result = {
              status: isHolidayOrWeekend ? "Holiday" : statusIfAbsent,
              totalWorkMs: null,
              breakMs: null,
            };

            const { status, totalWorkMs, breakMs } = result;

            // Filter based on report type and attendance type
            let includeRow = false;
            if (attendanceType === "outsource") {
              if (reportType === "attendance") {
                includeRow = false; // No entries for outsource attendance report
              } else if (reportType === "leaves") {
                includeRow = status !== "Present" && status !== "Holiday";
              }
            } else {
              if (reportType === "attendance") {
                includeRow = status !== "Holiday";
              } else if (reportType === "leaves") {
                includeRow = status !== "Present" && status !== "Holiday";
              }
            }

            if (includeRow) {
              formatted.push({
                id: emp.id,
                name: emp.name,
                date: DateTime.fromJSDate(date).setZone("Europe/London").toFormat("dd/MM/yyyy"),
                clock_in: "-",
                clock_out: "-",
                working_hours: formatHours(totalWorkMs),
                break_time: attendanceType === "outsource" ? "--" : formatHours(breakMs),
                status: attendanceType === "outsource" ? "Outsource" : status,
              });
            }
          } else {
            // Process each entry for this date
            entriesToProcess.forEach((entry) => {
              const result = calculateStatus(
                entry.clock_in,
                entry.clock_out,
                entry.break_start,
                entry.break_end,
                isHolidayOrWeekend,
                emp.id,
                date
              );

              const { status, totalWorkMs, breakMs } = result;

              // Filter based on report type and attendance type
              let includeRow = false;
              if (attendanceType === "outsource") {
                if (reportType === "attendance") {
                  includeRow = entry && entry.clock_in && entry.clock_out;
                } else if (reportType === "leaves") {
                  includeRow =
                    status !== "Present" &&
                    status !== "Holiday" &&
                    entry &&
                    entry.clock_in &&
                    entry.clock_out;
                }
              } else {
                if (reportType === "attendance") {
                  includeRow = status !== "Holiday";
                } else if (reportType === "leaves") {
                  includeRow = status !== "Present" && status !== "Holiday";
                }
              }

              if (includeRow) {
                let clockInDt = null;
                let clockOutDt = null;
                let breakStartDt = null;
                let breakEndDt = null;
                try {
                  clockInDt = entry?.clock_in ? DateTime.fromISO(entry.clock_in, { zone: "UTC" }).setZone("Europe/London") : null;
                } catch (error) {
                  console.error("Error parsing clock_in:", error);
                }
                try {
                  clockOutDt = entry?.clock_out ? DateTime.fromISO(entry.clock_out, { zone: "UTC" }).setZone("Europe/London") : null;
                } catch (error) {
                  console.error("Error parsing clock_out:", error);
                }
                try {
                  breakStartDt = entry?.break_start ? DateTime.fromISO(entry.break_start, { zone: "UTC" }).setZone("Europe/London") : null;
                } catch (error) {
                  console.error("Error parsing break_start:", error);
                }
                try {
                  breakEndDt = entry?.break_end ? DateTime.fromISO(entry.break_end, { zone: "UTC" }).setZone("Europe/London") : null;
                } catch (error) {
                  console.error("Error parsing break_end:", error);
                }

                formatted.push({
                  id: emp.id,
                  name: emp.name,
                  date: DateTime.fromJSDate(date).setZone("Europe/London").toFormat("dd/MM/yyyy"),
                  clock_in: clockInDt ? clockInDt.toFormat("HH:mm") : "-",
                  clock_out: clockOutDt ? clockOutDt.toFormat("HH:mm") : "-",
                  working_hours: formatHours(totalWorkMs),
                  break_time: attendanceType === "outsource" ? "--" : formatHours(breakMs),
                  status: attendanceType === "outsource" ? "Outsource" : status,
                });
              }
            });
          }
        });
      });

      setReportData(formatted);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Failed to generate report. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const calculateStatus = (
    clockIn,
    clockOut,
    breakStart,
    breakEnd,
    isHolidayOrWeekend,
    empIdForStatusCheck,
    dateForStatusCheck
  ) => {
    if (!clockIn || !clockOut) {
      let statusIfAbsent = "Absent";
      if (!isHolidayOrWeekend) {
        const leaveType = getLeaveTypeForDate(empIdForStatusCheck, dateForStatusCheck);
        if (leaveType) {
          statusIfAbsent = leaveType;
        }
      }
      return {
        status: isHolidayOrWeekend ? "Holiday" : statusIfAbsent,
        totalWorkMs: null,
        breakMs: null,
      };
    }

    const start = new Date(clockIn);
    const end = new Date(clockOut);

    // Validate dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return {
        status: "Absent",
        totalWorkMs: null,
        breakMs: null,
      };
    }

    let totalMinutes = (end - start) / 60000;
    let breakMinutes = 0;

    // Calculate break time in minutes
    if (breakStart && breakEnd) {
      const bStart = new Date(breakStart);
      const bEnd = new Date(breakEnd);

      if (!isNaN(bStart.getTime()) && !isNaN(bEnd.getTime())) {
        breakMinutes = Math.floor((bEnd - bStart) / 60000); // Floor to nearest minute
        if (breakMinutes > 0) {
          totalMinutes -= breakMinutes;
        } else {
          breakMinutes = 0;
        }
      }
    }

    // Floor total worked time to nearest minute
    totalMinutes = Math.floor(totalMinutes);

    const totalHours = totalMinutes / 60;
    let status = "Absent";

    // Determine status based on working hours and day type
    if (isHolidayOrWeekend) {
      // Working on weekend or holiday is always overtime
      if (totalHours > 0) {
        status = "Overtime";
      } else {
        status = "Holiday";
      }
    } else {
      // Regular working day
      const fullDayHours = adminSettings ? timeToHours(adminSettings.full_day_hours) : 7.5;
      const halfDayHours = adminSettings ? timeToHours(adminSettings.half_day_hours) : 4.5;
      const shortDayHours = adminSettings ? timeToHours(adminSettings.short_day_hours) : 0;

      if (totalHours >= fullDayHours) {
        status = "Present";
      } else if (totalHours >= halfDayHours) {
        status = "Half Day";
      } else if (totalHours > shortDayHours) {
        status = "Short Day";
      } else {
        status = "Absent";
      }
    }

    return { status, totalWorkMs: totalMinutes / 60, breakMs: breakMinutes / 60 };
  };

  const downloadCSV = () => {
    if (filteredData.length === 0) {
      showModal("Export Error", "No data to export", "warning");
      return;
    }

    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
      XLSX.writeFile(
        workbook,
        `attendance_report_${reportType}_${new Date().toISOString().split("T")[0]
        }.xlsx`
      );
    } catch (error) {
      console.error("Error downloading CSV:", error);
      showModal(
        "Export Error",
        "Failed to download CSV. Please try again.",
        "error"
      );
    }
  };

  const downloadPDF = () => {
    if (filteredData.length === 0) {
      showModal("Export Error", "No data to export", "warning");
      return;
    }

    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(`Attendance Report - ${reportType.toUpperCase()}`, 14, 20);

      // Add date range
      doc.setFontSize(12);
      doc.text(`From: ${fromDate} To: ${toDate}`, 14, 30);
      doc.text(`Records: ${filteredData.length}`, 14, 40);

      // Table headers
      const headers = [
        "ID",
        "Name",
        "Date",
        "Clock In",
        "Clock Out",
        "Hours",
        "Break",
        "Status",
      ];

      // Starting position
      let yPosition = 55;
      const lineHeight = 6;
      const colWidths = [15, 40, 25, 25, 25, 20, 20, 25];
      let xPosition = 14;

      // Draw headers
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      headers.forEach((header, index) => {
        doc.text(header, xPosition, yPosition);
        xPosition += colWidths[index];
      });

      // Draw header line
      doc.line(14, yPosition + 2, 195, yPosition + 2);
      yPosition += lineHeight + 2;

      // Draw data rows
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);

      filteredData.forEach((row) => {
        // Check if we need a new page
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;

          // Redraw headers on new page
          xPosition = 14;
          doc.setFont(undefined, "bold");
          doc.setFontSize(10);
          headers.forEach((header, index) => {
            doc.text(header, xPosition, yPosition);
            xPosition += colWidths[index];
          });
          doc.line(14, yPosition + 2, 195, yPosition + 2);
          yPosition += lineHeight + 2;
          doc.setFont(undefined, "normal");
          doc.setFontSize(8);
        }

        xPosition = 14;
        const rowData = [
          row.id.toString(),
          row.name && row.name.length > 18
            ? row.name.substring(0, 18) + "..."
            : row.name || "",
          row.date || "",
          row.clock_in || "",
          row.clock_out || "",
          row.working_hours || "",
          row.break_time || "",
          row.status || "",
        ];

        rowData.forEach((cell, index) => {
          doc.text(cell, xPosition, yPosition);
          xPosition += colWidths[index];
        });

        yPosition += lineHeight;
      });

      doc.save(
        `attendance_report_${reportType}_${new Date().toISOString().split("T")[0]
        }.pdf`
      );
    } catch (error) {
      console.error("Error downloading PDF:", error);
      showModal(
        "Export Error",
        "Failed to download PDF. Please try again.",
        "error"
      );
    }
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return "--:--";

    try {
      const londonTime = DateTime.fromISO(dateTime, {
        zone: "Europe/London",
      });

      return londonTime.toFormat("HH:mm");
    } catch (err) {
      console.error("Time format error:", err);
      return "--:--";
    }
  };

  return (
    <div className="report-page">
      <h2 className="report-title">
        <FileText size={20} /> Generate Reports
      </h2>

      {error && (
        <div
          className="error-message"
          style={{ color: "red", marginBottom: "1rem" }}
        >
          {error}
        </div>
      )}

      <form className="report-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            <option value="attendance">Attendance</option>
            <option value="leaves">Leaves</option>
          </select>
        </div>

        <div className="form-group">
          <label>From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Attendance Type</label>
          <select
            value={attendanceType}
            onChange={(e) => setAttendanceType(e.target.value)}
          >
            <option value="employee">Employee Attendance</option>
            <option value="outsource">Outsource Attendance</option>
          </select>
        </div>

        <div className="form-group">
          <label>Employee</label>
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="all">
              {attendanceType === "employee" ? "All Employees" : "All Outsource Users"}
            </option>
            {getFilteredEmployees().map((emp) => (
              <option key={emp.user.id} value={emp.user.id}>
                {emp.user.id} - {emp.user.name}
              </option>
            ))}
          </select>
        </div>


        <button type="submit" className="generate-btn" disabled={loading}>
          {loading ? "Generating..." : "Generate"}
        </button>
      </form>

      {reportData.length > 0 && (
        <div className="report-results">
          <div className="results-header">
            <div className="results-info">
              <span>Total Records: {reportData.length}</span>
              <span>Filtered Records: {filteredData.length}</span>
            </div>

            <div className="results-actions">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="filter-toggle-btn"
              >
                <Filter size={16} />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <div className="download-buttons">
                <button onClick={downloadCSV}>
                  <Download size={16} /> XLSX
                </button>
                <button onClick={downloadPDF}>
                  <Download size={16} /> PDF
                </button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="filters-panel">
              <div className="filters-header">
                <h3>Advanced Filters</h3>
                <button onClick={clearFilters} className="clear-filters-btn">
                  <X size={16} /> Clear All
                </button>
              </div>

              <div className="filters-grid">
                {/* Employee & Status Filters */}
                <div className="filter-group">
                  <h4>Employee & Status</h4>

                  <div className="filter-item">
                    <label>Search Name</label>
                    <input
                      type="text"
                      placeholder="Employee name..."
                      value={filters.searchName}
                      onChange={(e) =>
                        handleFilterChange("searchName", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-item">
                    <label>Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                    >
                      <option value="all">All Status</option>
                      <option value="Present">Present</option>
                      <option value="Absent">Absent</option>
                      <option value="Half Day">Half Day</option>
                      <option value="Short Day">Short Day</option>
                      <option value="Overtime">Overtime</option>
                      <option value="Holiday">Holiday</option>
                      <option value="sick">Sick</option>
                      <option value="annual">Annual</option>
                      <option value="authorised">Authorised</option>
                      <option value="maternity">Maternity</option>
                      <option value="paternity">Paternity</option>
                      <option value="bereavement">Bereavement</option>
                    </select>
                  </div>

                  <div className="filter-item">
                    <label>Day of Week</label>
                    <select
                      value={filters.dayOfWeek}
                      onChange={(e) =>
                        handleFilterChange("dayOfWeek", e.target.value)
                      }
                    >
                      <option value="all">All Days</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                      <option value="0">Sunday</option>
                    </select>
                  </div>
                </div>

                {/* Special Conditions & Sorting */}
                <div className="filter-group">
                  <h4>Special Conditions</h4>

                  <div className="filter-item checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={filters.lateArrivals}
                        onChange={(e) =>
                          handleFilterChange("lateArrivals", e.target.checked)
                        }
                      />
                      Late Arrivals (After 9:00 AM)
                    </label>
                  </div>

                  <div className="filter-item checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={filters.earlyDepartures}
                        onChange={(e) =>
                          handleFilterChange(
                            "earlyDepartures",
                            e.target.checked
                          )
                        }
                      />
                      Early Departures (Before 5:00 PM)
                    </label>
                  </div>

                  <div className="filter-item checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={filters.overtime}
                        onChange={(e) =>
                          handleFilterChange("overtime", e.target.checked)
                        }
                      />
                      Overtime Only
                    </label>
                  </div>

                  <div className="filter-item checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={filters.perfectAttendance}
                        onChange={(e) =>
                          handleFilterChange(
                            "perfectAttendance",
                            e.target.checked
                          )
                        }
                      />
                      Perfect Attendance Only
                    </label>
                    <div className="filter-item checkbox-item"></div>
                  </div>
                  <div className="filter-item checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={filters.workingDaysOnly}
                        onChange={(e) =>
                          handleFilterChange(
                            "workingDaysOnly",
                            e.target.checked
                          )
                        }
                      />
                      Working Days Only
                    </label>
                  </div>

                  <div className="filter-item checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={filters.nonWorkingDaysOnly}
                        onChange={(e) =>
                          handleFilterChange(
                            "nonWorkingDaysOnly",
                            e.target.checked
                          )
                        }
                      />
                      Non-Working Days Only
                    </label>
                  </div>

                  <div className="filter-item">
                    <label>Sort By</label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) =>
                        handleFilterChange("sortBy", e.target.value)
                      }
                    >
                      <option value="date">Date</option>
                      <option value="name">Employee Name</option>
                      <option value="working_hours">Working Hours</option>
                      <option value="status">Status</option>
                      <option value="clock_in">Clock In Time</option>
                    </select>
                  </div>

                  <div className="filter-item">
                    <label>Sort Order</label>
                    <select
                      value={filters.sortOrder}
                      onChange={(e) =>
                        handleFilterChange("sortOrder", e.target.value)
                      }
                    >
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Clock In</th>
                  <th>Clock Out</th>
                  <th>Working Hours</th>
                  <th>Break Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.length > 0 ? (
                  currentRecords.map((row, index) => (
                    <tr key={`${row.id}-${row.date}-${index}`}>
                      <td>{row.id}</td>
                      <td>{row.name}</td>
                      <td>{row.date}</td>
                      <td>{row.clock_in}</td>
                      <td>{row.clock_out}</td>
                      <td>{row.working_hours}</td>
                      <td>{row.break_time}</td>
                      <td
                        data-status={row.status.toLowerCase().replace(" ", "-")}
                      >
                        {row.status}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data">
                      No records found matching the selected criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredData.length > 0 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Showing {indexOfFirstRecord + 1} to{" "}
                  {Math.min(indexOfLastRecord, filteredData.length)} of{" "}
                  {filteredData.length} records
                </span>

                <div className="records-per-page">
                  <label>Records per page:</label>
                  <select
                    value={recordsPerPage}
                    onChange={(e) => {
                      setRecordsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="pagination-buttons">
                  <button
                    onClick={() => paginate(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </button>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum =
                      currentPage <= 3
                        ? i + 1
                        : currentPage >= totalPages - 2
                          ? totalPages - 4 + i
                          : currentPage - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={currentPage === pageNum ? "active" : ""}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => paginate(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </div>
  );
};

export default EmpReports;
